import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Presentation only: the full sorted source remains with the caller/export.
export function tableWindowRange(count: number, top: number, height = 480, rowHeight = 36, fixedHeight = 88) {
  if (count <= 120) return { start: 0, end: count, before: 0, after: 0 };
  const visible = Math.max(1, Math.ceil(Math.max(0, height - fixedHeight) / rowHeight));
  const first = Math.min(Math.max(0, count - visible), Math.max(0, Math.floor(top / rowHeight)));
  const start = Math.max(0, first - 8);
  const end = Math.min(count, first + visible + 8);
  return { start, end, before: start * rowHeight, after: (count - end) * rowHeight };
}

export function useTableWindow(count: number, resetKey: string) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);
  const [viewport, setViewport] = useState({ top: 0, height: 480, rowHeight: 36, fixedHeight: 88 });
  const measure = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    const css = getComputedStyle(node);
    const rowHeight = parseFloat(css.getPropertyValue("--overview-row")) || 36;
    const fixedHeight = (parseFloat(css.getPropertyValue("--overview-header")) || 48)
      + (parseFloat(css.getPropertyValue("--overview-total")) || 40);
    const next = { top: Math.floor(node.scrollTop / rowHeight) * rowHeight, height: node.clientHeight || 480, rowHeight, fixedHeight };
    setViewport(old => Object.keys(next).every(key => old[key as keyof typeof old] === next[key as keyof typeof next]) ? old : next);
  }, []);
  const onScroll = useCallback(() => {
    if (frame.current != null) return;
    frame.current = requestAnimationFrame(() => { frame.current = null; measure(); });
  }, [measure]);
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node || count <= 120) return;
    node.scrollTop = 0;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (frame.current != null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [count, resetKey, measure]);
  return { scrollRef, onScroll: count > 120 ? onScroll : undefined,
    ...tableWindowRange(count, viewport.top, viewport.height, viewport.rowHeight, viewport.fixedHeight) };
}

export function TableWindowSpacer({ height, columns }: { height: number; columns: number }) {
  return height ? <tr aria-hidden="true" role="presentation" className="table-window-spacer"><td colSpan={columns}
    style={{ height, padding: 0, border: 0, lineHeight: 0 }} /></tr> : null;
}
